import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AuthError, signIn, verifyOtp } from '@radiology/api-client';
import { AuthLayout } from './AuthLayout';

type Step =
  { name: 'credentials' } | { name: 'code'; otpTicket: string; email: string; expiresIn: number };

/**
 * Sign-in: password, then the emailed code.
 *
 * Two steps, one screen. The password is submitted once and never again — the
 * ticket returned by step 1 is what authorises step 2 (this is what closes S10,
 * where the legacy flow resent the password at the verify step).
 */
export function SignInScreen({ onReset }: { onReset: () => void }) {
  const [step, setStep] = useState<Step>({ name: 'credentials' });
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(identifier.trim(), password);
      setStep({
        name: 'code',
        otpTicket: result.otpTicket,
        // The address the server actually sent to — not what was typed, which
        // may have been a username.
        email: result.email,
        expiresIn: result.expiresIn,
      });
      setPassword(''); // Not needed again; do not keep it in memory.
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step.name !== 'code') return;
    setBusy(true);
    setError(null);
    try {
      await verifyOtp(step.otpTicket, code.trim());
      // SessionProvider picks the new session up via onAuthStateChange.
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Something went wrong.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (step.name === 'code') {
    return (
      <AuthLayout
        title="Enter your code"
        description={
          <>
            We sent a {String(6)}-digit code to <strong>{step.email}</strong>. It expires in{' '}
            {Math.round(step.expiresIn / 60)} minutes.
          </>
        }
        footer={
          <Typography variant="caption" color="text.secondary">
            Didn&apos;t get it? Check your spam folder, or{' '}
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => {
                setStep({ name: 'credentials' });
                setCode('');
                setError(null);
              }}
            >
              start again
            </Link>
            .
          </Typography>
        }
      >
        <form onSubmit={e => void submitCode(e)}>
          <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  style: { letterSpacing: '0.4em', fontSize: '1.15rem', fontWeight: 600 },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || code.length < 6}
            >
              {busy ? 'Verifying…' : 'Sign in'}
            </Button>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      description="Use your username or work email address, and your password."
    >
      <form onSubmit={e => void submitCredentials(e)}>
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Username or email"
            // Not type="email": the browser would reject a bare username as
            // malformed before the form ever submits.
            type="text"
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            autoFocus
            autoComplete="username"
            required
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy || !identifier || !password}
          >
            {busy ? 'Checking…' : 'Continue'}
          </Button>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onReset}
            sx={{ mx: 'auto' }}
          >
            Forgot your password?
          </Link>
        </Stack>
      </form>
    </AuthLayout>
  );
}
